import * as Sentry from '@sentry/nextjs'
import logger from 'src/lib/logger'
import merge from 'deepmerge'


type RawContext = {
  errorInfo: Object
  tags: Object
  ignoreInEnv?: string
  level?: Sentry.Severity
  req?: any
  res?: any
  query?: string
  pathname?: string
}

/**
 * normalizeError
 *
 * this function attempts to normalize what we're capturing. For e.g. graphqQLError arrays do
 * not often cleanly ingest into sentry. This would normalize the error message and add some tags.
 *
 * @param {*} rawError - some entity that we want to capture. I.e. Error, Array, String, etc.
 * @param {*} rawContext - the context we pass to captureException to make it fancy
 */
function normalizeError(rawError, rawContext: RawContext = { errorInfo: {}, tags: {} }) {
  try {
    // GraphQL error
    // these come in as arrays. Normalize the message as first error and then rest as extra-info.
    if (Array.isArray(rawError) && rawError?.[0]?.message) {
      // Instantiating a new error here to create a stack trace to try and make it more useful?
      const error = new Error(rawError[0].message);

      // Can't simply deepmerge the whole thing because of the `req` instance
      rawContext.errorInfo = merge(rawContext.errorInfo, {
        graphqlErrors: JSON.stringify(rawError, null, 2),
      });
      rawContext.tags = merge(rawContext.tags, { graphql: true });
      return { error, context: rawContext };
    }
  } catch (err) {
    // well, thats not good. Ignore and let the original capture through as planned.
  }

  return {
    error: rawError,
    context: rawContext,
  };
}

const isBrowser = !(typeof window === "undefined")

export const captureException = async function captureException(
  rawError,
  rawContext?,
) {
  const { error, context } = normalizeError(rawError, rawContext);

  // Log rawError here for thre trace
  if (
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === 'development' &&
    process.env.NODE_ENV !== 'test'
  ) {
    console.error(rawError);
  }

  // This little flag allows us to throw errors as part of flow control that might have already been captured and
  // avoid double-capturing with potentially different stack traces leading to confusion.
  if (error?.noCapture) return;

  // avoid capturing the same error twice.
  if (error?.alreadyCaptured) return;
  try {
    error.alreadyCaptured = true;
  } catch (e) {}

  try {
    if (context?.ignoreInEnv === process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT)
      return;

    Sentry.withScope((scope) => {
      try {
        if (context?.level) {
          scope.setLevel(context.level);
        }
        if (context?.tags) {
          Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
        }

        scope.setTag('ssr', !isBrowser);

        if (error?.message) {
          // De-duplication currently doesn't work correctly for SSR / browser
          // errors so we force de-duplication by error message if it is present
          scope.setFingerprint([error.message]);
        }

        if (error.statusCode) {
          scope.setExtra('statusCode', error.statusCode);
        }

        if (context) {
          const { req, res, errorInfo, query, pathname } = context;

          if (res && res.statusCode) {
            scope.setExtra('statusCode', res.statusCode);
          }

          if (isBrowser) {
            scope.setExtra('query', query);
            scope.setExtra('pathname', pathname);
          } else {
            if (req) {
              scope.setExtra('url', req.url);
              scope.setExtra('method', req.method);
              scope.setExtra('headers', req.headers);
              scope.setExtra('params', req.params);
              scope.setExtra('query', req.query);
            }
          }

          Object.entries(errorInfo || {}).forEach(([k, v]) =>
            scope.setExtra(k, v),
          );
        }
        const errorId = Sentry.captureException(error);

        // On the server, lets log a simple message for visibility
        if (!isBrowser) {
          const message = error.message || '[no message]';
          logger.error({ message, errorId });
        }
      } catch (error) {
        // @note: fail silently in browser in case Sentry fails
        if (!isBrowser) logger.error(error);
      }
    });
  } catch (error) {
    if (!isBrowser) logger.error(error);
  }
};

/**
 * Only works on client.
 * Sets user on scope for every sentry error.
 * @param {string} id
 */
export const setExceptionUser = function setExceptionUser(id) {
  if (!isBrowser) return;
  Sentry.configureScope((scope) => scope.setUser({ id }));
};

export const SEVERITY = Sentry.Severity;
